import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  Card,
  Icon,
  Stack,
  Typography,
  styled,
  Box,
  useTheme,
  IconButton,
  Palette
} from "@mui/material";
import {
  Attention20Px,
  CheckmarkCircle24Px,
  Error24Px,
  NavbarFilters
} from "@qubit/autoparts";
import dayjs from "dayjs";
import { jsPDF } from "jspdf";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";

import { TESTING_TOOL_STACK_BREAKPOINT } from "./TestingToolLayout";
import {
  clearEventsFeedFilters,
  eventFeedFiltersList,
  selectEventFeed,
  selectEventsFeedFilters,
  setEventsFeedFilters,
  testingToolList
} from "./testingTools.slice";
import { EventStatus, TestingToolWithAll } from "./testingTools.types";
import {
  createEventFeedReportByTestCategory,
  eventFeedReportMargin
} from "./testingToolsUtils";

const EventFeedContainer = styled(Stack)(
  ({ theme: { palette, shadows, spacing } }) => ({
    boxShadow: shadows["3"],
    backgroundColor: palette.secondary.light,
    borderRadius: 10,
    alignSelf: "stretch",
    justifySelf: "stretch",
    flexDirection: "column",
    minWidth: 0,
    height: "90%",
    minHeight: 0,
    boxSizing: "border-box",
    overflow: "hidden",
    gridRow: "2/3",
    gridColumn: "2/3",
    gap: spacing(1),
    padding: spacing(2),
    [`@media (max-width: ${TESTING_TOOL_STACK_BREAKPOINT})`]: {
      gridColumn: "auto",
      gridRow: "auto",
      height: "auto"
    }
  })
);

const EventFeedContent = styled(Box)(({ theme: { spacing } }) => ({
  padding: spacing(1),
  height: "100%",
  overflow: "auto",
  "&::-webkit-scrollbar": {
    display: "none"
  },
  scrollbarWidth: "none",
  msOverflowStyle: "none"
}));

const EventFeedMessagesContainer = styled(Stack)(({ theme: { spacing } }) => ({
  gap: spacing(1.5),
  flexDirection: "column-reverse",
  justifyContent: "flex-end"
}));

export const getEventMessageStyling = (
  state: EventStatus,
  palette: Palette
) => {
  switch (state) {
    case "Success":
      return {
        icon: (
          <CheckmarkCircle24Px
            style={{
              fill: palette.success.main,
              height: "25px",
              width: "25px"
            }}
          />
        ),
        backgroundColor: "success.light"
      };
    case "Failed":
      return {
        icon: (
          <Error24Px
            style={{
              fill: palette.error.main,
              height: "25px",
              width: "25px"
            }}
          />
        ),
        backgroundColor: "error.light"
      };
    case "Info":
    default:
      return {
        icon: (
          <Attention20Px
            style={{
              fill: palette.primary.main,
              height: "25px",
              width: "25px"
            }}
          />
        ),
        backgroundColor: "primary.light"
      };
  }
};

const EventFeed = () => {
  const dispatch = useAppDispatch();
  const events = useAppSelector(selectEventFeed);
  const selectedEventFeedFilters = useAppSelector(selectEventsFeedFilters);
  const { t } = useTranslation();
  const { palette } = useTheme();

  const eventFilterStateTypes = Object.keys(
    eventFeedFiltersList
  ) as EventStatus[];
  const eventFilterMenuList = eventFilterStateTypes.map(
    (filterOptionKey: EventStatus) => {
      const filterOptionValue = eventFeedFiltersList[filterOptionKey];
      return {
        translation: t(filterOptionValue),
        filter: filterOptionValue,
        icon: getEventMessageStyling(filterOptionKey, palette).icon
      };
    }
  );

  const eventsToDisplay = useMemo(() => {
    if (selectedEventFeedFilters.length)
      return events.filter((event) => {
        return (
          event.state &&
          selectedEventFeedFilters.includes(eventFeedFiltersList[event.state])
        );
      });
    return events;
  }, [events, selectedEventFeedFilters]);

  const createEventFeedReport = () => {
    const eventFeedReportDocument = new jsPDF({
      orientation: "portrait",
      format: "a4"
    });
    let lineCoordinateCount = eventFeedReportMargin;

    eventFeedReportDocument.setFontSize(16);
    eventFeedReportDocument.setFont("helvetica", "bold");
    eventFeedReportDocument.text(
      [
        `Qubit - Consolidated Test Execution Report for All Testing Tools`,
        `${dayjs().format("YYYY-MM-DD")}`
      ],
      100,
      lineCoordinateCount,
      {
        align: "center"
      }
    );

    lineCoordinateCount += 25;

    const testCategoryTypesToDisplay: TestingToolWithAll[] = [
      ...testingToolList,
      "All"
    ];
    eventFeedReportDocument.setFont("helvetica", "normal");
    testCategoryTypesToDisplay.forEach((testNameOrigin) => {
      const { nextLineCoordinateCount } = createEventFeedReportByTestCategory(
        testNameOrigin,
        events,
        eventFeedReportDocument,
        lineCoordinateCount
      );
      if (
        lineCoordinateCount < nextLineCoordinateCount &&
        testNameOrigin !== "All"
      )
        lineCoordinateCount = nextLineCoordinateCount + 5;
      else lineCoordinateCount = nextLineCoordinateCount;
    });
    const pageCount = eventFeedReportDocument.getNumberOfPages();

    const pageWidth = eventFeedReportDocument.internal.pageSize.width;
    const pageHeight = eventFeedReportDocument.internal.pageSize.height;

    for (let pageNumber = 1; pageNumber <= pageCount; pageNumber++) {
      eventFeedReportDocument.setPage(pageNumber);
      eventFeedReportDocument.setFontSize(9);
      eventFeedReportDocument.setFont("helvetica", "normal");
      eventFeedReportDocument.text(
        `Page ${pageNumber} of ${pageCount}`,
        pageWidth / 2,
        pageHeight - 5,
        {
          align: "center"
        }
      );
    }
    const pdfBlob = eventFeedReportDocument.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);

    const eventFeedReportTab = window.open(pdfUrl, "_blank");

    if (eventFeedReportTab) {
      const revokeUrlOnClose = () => {
        URL.revokeObjectURL(pdfUrl);
      };
      eventFeedReportTab.addEventListener("beforeunload", revokeUrlOnClose);
      //revoke object url when user closes tab
      eventFeedReportTab.onload = () => {
        eventFeedReportTab.removeEventListener(
          "beforeunload",
          revokeUrlOnClose
        );
      };
    }
  };

  return (
    <EventFeedContainer>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          justifyItems: "center",
          alignItems: "center"
        }}
      >
        <Box
          sx={{
            gridColumn: "2/5",
            margin: "0 auto"
          }}
        >
          <Typography
            sx={{
              color: "darkGray.dark"
            }}
            variant="h6"
            id="event-feed"
          >
            {t("event feed")}
          </Typography>
        </Box>
        <Stack
          sx={{
            gridColumn: "-1",
            gap: 1,
            flexDirection: "row"
          }}
        >
          <NavbarFilters
            menuItemList={eventFilterMenuList}
            selectedMenuItems={selectedEventFeedFilters}
            menuItemsTitle={t("event type")}
            onFilterData={(filters) => {
              dispatch(setEventsFeedFilters(filters));
            }}
            onResetFilters={() => {
              dispatch(clearEventsFeedFilters());
            }}
          />
          <IconButton
            aria-label="event feed report export button"
            onClick={createEventFeedReport}
            disabled={!events.length}
          >
            <OpenInNewIcon sx={{ color: "secondary.contrastText" }} />
          </IconButton>
        </Stack>
      </Box>
      <EventFeedContent role="region" aria-labelledby="event-feed">
        {eventsToDisplay.length > 0 ? (
          <EventFeedMessagesContainer>
            {eventsToDisplay.map((event, i) => {
              const { icon, backgroundColor } = getEventMessageStyling(
                event.state || "Info",
                palette
              );
              return (
                <Card
                  sx={{ p: 1, backgroundColor, minHeight: 50 }}
                  key={`event-message-${i}`}
                >
                  <Stack flexDirection="row" alignItems="center">
                    <Icon
                      sx={{
                        height: 40,
                        width: 40,
                        m: "10px 5px 0px 0px"
                      }}
                    >
                      {icon}
                    </Icon>
                    <Stack
                      flexDirection="column"
                      sx={{
                        // give message text a stable width so it wraps predictably
                        flex: 1,
                        minWidth: 0
                      }}
                    >
                      <Typography variant="body1">{event.text}</Typography>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: "darkGray.main",
                          fontWeight: "400"
                        }}
                      >
                        {dayjs(event.time).format("MMMM Do, h:mm:ss a")}
                      </Typography>
                    </Stack>
                  </Stack>
                </Card>
              );
            })}
          </EventFeedMessagesContainer>
        ) : (
          <Typography
            sx={{
              fontStyle: "italic"
            }}
          >
            {t("no events found")}
          </Typography>
        )}
      </EventFeedContent>
    </EventFeedContainer>
  );
};

export default EventFeed;
